var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useLocation, useNavigate } from 'react-router-dom';
import { match, compile } from 'path-to-regexp';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { hasValue, isUserAllowed, toBool } from '@utils';
import { UXPContext } from '@core/context/Context';
import { getDashboardDetails } from '@core/services/services';
import { findDashboardLink, isDashboardLinkAllowed } from '@core/layout/navigationUtils';
import { clientUnavailableReason } from '@core/layout/pageRequirements';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
const isDashboardPath = (p) => p === '/dashboard' || p.startsWith('/dashboard/');
/** Internal hook — not for use outside the App component. */
export function useRoutes() {
    var _a, _b;
    const context = useContext(UXPContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [dashboardConfig, setDashboardConfig] = useState(null);
    // The resolvedDashboardPath whose fetch has settled (success OR failure). Derived
    // "pending" (below) compares this to the current path, so it's correct on the very
    // first render — unlike a loading boolean that only flips inside a post-paint effect.
    const [settledPath, setSettledPath] = useState(null);
    // The dashboard loaded fine but its Allowed User Groups exclude this user.
    const [dashboardDenied, setDashboardDenied] = useState(false);
    const sortRoutes = useCallback((paths, direction = 'desc') => {
        return paths.sort((a, b) => {
            const aSegments = a.split('/').filter(Boolean);
            const bSegments = b.split('/').filter(Boolean);
            const segmentDiff = aSegments.length - bSegments.length;
            if (segmentDiff !== 0) {
                return direction === 'asc' ? segmentDiff : -segmentDiff;
            }
            // Fewer dynamic segments = more specific match
            const aParamCount = aSegments.filter(s => s.startsWith(':')).length;
            const bParamCount = bSegments.filter(s => s.startsWith(':')).length;
            return aParamCount - bParamCount;
        });
    }, []);
    const normalizeRoutes = useCallback((routes) => {
        // Safety net: strip query strings from route keys — the server keys routes on the
        // link's PATH, but path-to-regexp treats ? as an optional parameter marker and throws if
        // one ever reaches us inside a route pattern.
        const sanitized = {};
        const collidedKeys = new Set();
        for (const [path, config] of Object.entries(routes)) {
            const key = path.split('?')[0];
            // A key path-to-regexp cannot parse (e.g. a full URL saved as a nav link, which the
            // editor turns into `/viewhttps://host/x`) throws inside the match() loops below and
            // takes the whole app down. Drop it here: that one link stops working, the rest of
            // navigation keeps working.
            try {
                match(key);
            }
            catch (_a) {
                console.warn('[uxp] Skipping invalid route pattern:', path);
                continue;
            }
            // Two links on one path (they differ only by query string) collapse to one route.
            // Keep the FIRST rather than letting the last silently win, and say so once per path
            // — the route's page and permissions come from that first link.
            if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
                if (!collidedKeys.has(key)) {
                    collidedKeys.add(key);
                    console.warn(`[uxp] Two navigation links share the route "${key}" (they differ only by query string). Keeping the first; "${path}" is ignored for routing.`);
                }
                continue;
            }
            sanitized[key] = config;
        }
        const sortedPaths = sortRoutes(Object.keys(sanitized), 'asc');
        const normalized = {};
        for (const path of sortedPaths) {
            const segments = path.split('/').filter(Boolean);
            let inheritedGroups = [];
            let inheritedRoles = [];
            let parentPath = '';
            for (const segment of segments.slice(0, -1)) {
                parentPath += `/${segment}`;
                const parent = normalized[parentPath];
                if (parent) {
                    inheritedGroups = [...new Set([...inheritedGroups, ...(parent.userGroups || [])])];
                    inheritedRoles = [...new Set([...inheritedRoles, ...(parent.appRoles || [])])];
                }
            }
            const current = sanitized[path];
            normalized[path] = Object.assign(Object.assign({}, current), { userGroups: [...new Set([...(current.userGroups || []), ...inheritedGroups])], appRoles: [...new Set([...(current.appRoles || []), ...inheritedRoles])] });
        }
        return normalized;
    }, [sortRoutes]);
    const allRoutes = useMemo(() => normalizeRoutes(context.routes || {}), [context.routes]);
    const isDashboard = useMemo(() => {
        return isDashboardPath(location.pathname);
    }, [location.pathname]);
    /*
     * The dashboard URL whose portal config should be fetched — either a real
     * /dashboard/<id>[/<subId>] browser URL, or the compiled target of a symlink
     * route pointing at one. A symlinked dashboard renders the portal's widget
     * grid at the vanity /view/... URL while keeping the regular app navigation:
     * isDashboard stays URL-based, so the dashboard nav takeover never fires.
     */
    const resolvedDashboardPath = useMemo(() => {
        var _a, _b;
        const { pathname } = location;
        if (isDashboardPath(pathname))
            return pathname;
        const sortedPaths = sortRoutes(Object.keys(allRoutes), 'desc');
        for (const path of sortedPaths) {
            // Backstop for a bad pattern that reached us some other way — normalizeRoutes
            // already drops them. Never let one route kill the whole matcher.
            let matched;
            try {
                matched = match(path, { decode: decodeURIComponent })(pathname);
            }
            catch (_c) {
                continue;
            }
            if (!matched)
                continue;
            const config = allRoutes[path];
            if (config.type === 'symlink' && ((_b = (_a = config.metadata) === null || _a === void 0 ? void 0 : _a.symlink) === null || _b === void 0 ? void 0 : _b.target)) {
                try {
                    const params = Object.assign(Object.assign({}, (matched.params || {})), (config.metadata.symlink.params || {}));
                    let targetUrl = compile(config.metadata.symlink.target)(params);
                    if (!targetUrl.startsWith('/'))
                        targetUrl = `/${targetUrl}`;
                    if (targetUrl.startsWith('/dashboard'))
                        return targetUrl;
                }
                catch ( /* params missing — not a resolvable dashboard symlink */_d) { /* params missing — not a resolvable dashboard symlink */ }
            }
            // Most-specific match wins, same as matchedRoute below.
            return null;
        }
        return null;
    }, [location.pathname, allRoutes, sortRoutes]);
    // Latest resolvedDashboardPath, readable from inside an in-flight fetch. Two fetches can
    // resolve out of order; the stale one must not write its result over the current path's.
    const resolvedDashboardPathRef = useRef(resolvedDashboardPath);
    resolvedDashboardPathRef.current = resolvedDashboardPath;
    const fetchDashboardConfig = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        if (!context || !resolvedDashboardPath)
            return;
        const requestedPath = resolvedDashboardPath;
        /*
         * Dashboard URLs: /dashboard/<id>[/<subId>]
         */
        const parts = resolvedDashboardPath.split('/').filter(Boolean);
        const dashboardId = parts[1];
        const subDashboardId = parts.slice(2).join('/') || 'default';
        if (!dashboardId) {
            setDashboardConfig(null);
            setSettledPath(resolvedDashboardPath);
            return;
        }
        const details = yield getDashboardDetails(context, dashboardId, subDashboardId);
        // The path changed while this fetch was in flight — a later fetch owns the state now.
        if (resolvedDashboardPathRef.current !== requestedPath)
            return;
        if (!details) {
            // Anonymous visitor on a dashboard that doesn't exist (or isn't visible to them):
            // deny so App sends them to login, matching the server's behaviour on a hard load.
            if (context.isPublicSession) {
                context.setPublicCredentials('', '');
                setDashboardDenied(true);
            }
            setDashboardConfig(null);
            setSettledPath(resolvedDashboardPath);
            return;
        }
        // Allowed User Groups on the dashboard (UXPPortals.Roles): public -> anyone; empty -> any
        // logged-in user; otherwise the user's group must be listed. An anonymous session on a
        // non-public dashboard is denied here, before anything renders; App.tsx performs the
        // login redirect.
        const d = details.details;
        const allowed = toBool(d === null || d === void 0 ? void 0 : d.IsPublic)
            || (!context.isPublicSession && isUserAllowed(context, (d === null || d === void 0 ? void 0 : d.ParsedRoles) || [], []));
        /*
         * A public dashboard runs as its public user (setPublicCredentials falls back to the account
         * public user when it has none). An anonymous session takes that user's credentials here,
         * before anything renders, so the grid, a View link and a Symlink all make their first call
         * with them.
         */
        if (context.isPublicSession) {
            const runAs = allowed && hasValue(d === null || d === void 0 ? void 0 : d.PublicUserAPIKey);
            context.setPublicCredentials(runAs ? d.PublicUserAPIKey : '', runAs ? (d.PublicUserKey || '') : '');
        }
        if (!allowed) {
            setDashboardDenied(true);
            setDashboardConfig(null);
            setSettledPath(resolvedDashboardPath);
            return;
        }
        details.subDashboardId = subDashboardId;
        const deny = () => {
            setDashboardDenied(true);
            setDashboardConfig(null);
            setSettledPath(resolvedDashboardPath);
        };
        /*
         * The Links-tab link that owns this section URL (either stored format). Its User Groups /
         * App Roles, and every restricted parent's, apply on top of the dashboard's own access.
         * A View link renders its page here; a Symlink renders its target, as in navigation.
         */
        const owner = findDashboardLink(d, resolvedDashboardPath);
        if (owner && !isDashboardLinkAllowed(context, owner.entry))
            return deny();
        const ownerItem = owner === null || owner === void 0 ? void 0 : owner.entry.item;
        if ((owner === null || owner === void 0 ? void 0 : owner.entry.type) === 'view' && (ownerItem === null || ownerItem === void 0 ? void 0 : ownerItem.pageID)) {
            details.viewLink = { pageId: ownerItem.pageID, configuredProps: ownerItem.configuredProps };
        }
        else if ((owner === null || owner === void 0 ? void 0 : owner.entry.type) === 'symlink' && ((_b = (_a = ownerItem === null || ownerItem === void 0 ? void 0 : ownerItem.metadata) === null || _a === void 0 ? void 0 : _a.symlink) === null || _b === void 0 ? void 0 : _b.target)) {
            const params = Object.assign(Object.assign({}, owner.params), (ownerItem.metadata.symlink.params || {}));
            let targetUrl = null;
            try {
                targetUrl = compile(ownerItem.metadata.symlink.target)(params);
            }
            catch ( /* params missing */_f) { /* params missing */ }
            if (targetUrl && !targetUrl.startsWith('/'))
                targetUrl = `/${targetUrl}`;
            if (targetUrl && isDashboardPath(targetUrl)) {
                // Target dashboard: its own access and its own link restrictions decide.
                const tParts = targetUrl.split('/').filter(Boolean);
                const tSection = tParts.slice(2).join('/') || 'default';
                const target = tParts[1] ? yield getDashboardDetails(context, tParts[1], tSection) : null;
                if (resolvedDashboardPathRef.current !== requestedPath)
                    return;
                const td = target === null || target === void 0 ? void 0 : target.details;
                const tOwner = td ? findDashboardLink(td, targetUrl) : null;
                const tAllowed = !!td
                    && (toBool(td.IsPublic) || (!context.isPublicSession && isUserAllowed(context, td.ParsedRoles || [], [])))
                    && (!tOwner || isDashboardLinkAllowed(context, tOwner.entry));
                if (!tAllowed)
                    return deny();
                if ((tOwner === null || tOwner === void 0 ? void 0 : tOwner.entry.type) === 'view' && ((_c = tOwner.entry.item) === null || _c === void 0 ? void 0 : _c.pageID)) {
                    details.viewLink = { pageId: tOwner.entry.item.pageID, configuredProps: tOwner.entry.item.configuredProps };
                }
                else {
                    target.subDashboardId = tSection;
                    details.symlinkDashboard = target;
                    // The target's grid runs as the target's own public user, as before.
                    if (context.isPublicSession && hasValue(td.PublicUserAPIKey)) {
                        context.setPublicCredentials(td.PublicUserAPIKey, td.PublicUserKey || '');
                    }
                }
            }
            else {
                // Target app route: the user's routes are already filtered by the server.
                let route = null;
                if (targetUrl) {
                    for (const p of sortRoutes(Object.keys(allRoutes), 'desc')) {
                        try {
                            if (match(p, { decode: decodeURIComponent })(targetUrl)) {
                                route = allRoutes[p];
                                break;
                            }
                        }
                        catch (_g) {
                            continue;
                        }
                    }
                }
                const restricted = !route && !!targetUrl && (context.restrictedRoutes || []).some(p => {
                    try {
                        return !!match(p.split('?')[0], { decode: decodeURIComponent })(targetUrl);
                    }
                    catch (_a) {
                        return false;
                    }
                });
                if (restricted)
                    return deny();
                // Not found → empty pageId → "Unable to find the view", like a broken navigation symlink.
                details.viewLink = { pageId: route === null || route === void 0 ? void 0 : route.pageId, configuredProps: route === null || route === void 0 ? void 0 : route.configuredProps, params };
            }
        }
        setDashboardDenied(false);
        setDashboardConfig({
            title: ((_d = details === null || details === void 0 ? void 0 : details.details) === null || _d === void 0 ? void 0 : _d.Title) || 'Dashboard',
            pageId: '__dashboard__',
            appRoles: [],
            userGroups: ((_e = details === null || details === void 0 ? void 0 : details.details) === null || _e === void 0 ? void 0 : _e.ParsedRoles) || [],
            parameters: [],
            redirectTo: null,
            dashboarConfig: details,
        });
        setSettledPath(resolvedDashboardPath);
    }), [resolvedDashboardPath, allRoutes, sortRoutes]);
    useEffect(() => {
        if (!resolvedDashboardPath) {
            // Off dashboards the page's public user is set while rendering (below).
            setDashboardDenied(false);
            setDashboardConfig(null);
            // Reset the marker so returning to a dashboard later re-fetches instead of
            // treating a stale settledPath as "already settled".
            setSettledPath(null);
            return;
        }
        setDashboardDenied(false);
        fetchDashboardConfig();
    }, [resolvedDashboardPath, fetchDashboardConfig]);
    // A dashboard fetch is still outstanding for the current path. Derived (not a
    // lagging boolean) so it reads true on the first render of a hard reload.
    const dashboardPending = !!resolvedDashboardPath && settledPath !== resolvedDashboardPath;
    const matchedRoute = useMemo(() => {
        var _a, _b;
        const { pathname, search } = location;
        const query = Object.fromEntries(new URLSearchParams(search.replace(/^\?/, '')));
        // Only block rendering on the initial load (no existing config yet).
        // When navigating between sub-dashboards, keep the stale config visible
        // instead of returning null — returning null causes App to render
        // <BaseLayoutSkeleton /> which unmounts the entire tree on every link click.
        if (dashboardPending && !dashboardConfig)
            return null;
        // Dashboard fetched but this user's group is not allowed: report no match so the shell
        // shows Access Denied (isRestricted), instead of falling through to a symlink's own route.
        if (resolvedDashboardPath && dashboardDenied)
            return null;
        if (resolvedDashboardPath && dashboardConfig) {
            return { matchedPath: pathname, config: dashboardConfig, params: null, query };
        }
        const sortedPaths = sortRoutes(Object.keys(allRoutes), 'desc');
        for (const path of sortedPaths) {
            // Backstop — see resolvedDashboardPath above.
            let matched;
            try {
                matched = match(path, { decode: decodeURIComponent })(pathname);
            }
            catch (_c) {
                continue;
            }
            if (matched) {
                let config = allRoutes[path];
                let params = matched.params || {};
                /*
                 * Symlink: transparently serve the target route's content at this URL.
                 * Explicit params stored on the symlink override URL-extracted params.
                 */
                if (config.type === 'symlink' && ((_b = (_a = config.metadata) === null || _a === void 0 ? void 0 : _a.symlink) === null || _b === void 0 ? void 0 : _b.target)) {
                    try {
                        const resolvedParams = Object.assign(Object.assign({}, params), (config.metadata.symlink.params || {}));
                        const targetUrl = compile(config.metadata.symlink.target)(resolvedParams);
                        const sortedForTarget = sortRoutes(Object.keys(allRoutes), 'desc');
                        for (const targetPath of sortedForTarget) {
                            const targetMatch = match(targetPath, { decode: decodeURIComponent })(targetUrl);
                            if (targetMatch) {
                                // the target's content; a public symlink keeps its own public user
                                config = Object.assign(Object.assign(Object.assign({}, allRoutes[targetPath]), { type: 'symlink' }), (config.publicUserAPIKey
                                    ? { publicUserKey: config.publicUserKey, publicUserAPIKey: config.publicUserAPIKey }
                                    : {}));
                                params = resolvedParams;
                                break;
                            }
                        }
                    }
                    catch ( /* params missing — render symlink route as-is */_d) { /* params missing — render symlink route as-is */ }
                }
                return { matchedPath: path, config, params, query };
            }
        }
        return null;
    }, [location, allRoutes, sortRoutes, resolvedDashboardPath, dashboardConfig, dashboardPending, dashboardDenied]);
    /*
     * Off dashboards, a signed-out session runs as the public page's own public user
     * (setPublicCredentials falls back to the account public user). Set while rendering, not in an
     * effect: the page's own effects run before this hook's, and their first calls must already
     * carry the user.
     */
    const pageUserKey = ((_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.publicUserKey) || '';
    const pageUserAPIKey = ((_b = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _b === void 0 ? void 0 : _b.publicUserAPIKey) || '';
    const onDashboard = !!resolvedDashboardPath;
    useMemo(() => {
        // Entering a dashboard drops the page's own user at once; the dashboard sets its own once
        // fetched. Moving between dashboards keeps the shown dashboard's user until the next one loads.
        if (context === null || context === void 0 ? void 0 : context.isPublicSession) {
            context.setPublicCredentials(onDashboard ? '' : pageUserAPIKey, onDashboard ? '' : pageUserKey);
        }
        // the route's user is all that matters; context is the session's
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageUserAPIKey, pageUserKey, onDashboard]);
    /*
     * The URL matched none of the user's routes, but does match a route the backend
     * filtered out for this user (restrictedRoutes carries just the path patterns).
     * Distinguishes "forbidden" (NotAuthorized) from "doesn't exist" (NotFound).
     */
    const isRestricted = useMemo(() => {
        if (matchedRoute)
            return false;
        if (dashboardDenied)
            return true;
        return (context.restrictedRoutes || []).some(p => {
            // Strip query strings from keys — same reason as normalizeRoutes above.
            try {
                return !!match(p.split('?')[0], { decode: decodeURIComponent })(location.pathname);
            }
            catch (_a) {
                return false;
            }
        });
    }, [matchedRoute, dashboardDenied, context.restrictedRoutes, location.pathname]);
    /*
     * The page needs something missing here. The server lists the routes it turned off
     * (unavailableRoutes: path pattern -> reason), matched the same way as restrictedRoutes.
     * Otherwise the route's own page flags are checked in the browser.
     */
    const isMobile = useIsMobileLayout();
    const unavailableReason = useMemo(() => {
        for (const [p, reason] of Object.entries(context.unavailableRoutes || {})) {
            // Strip query strings from keys — same reason as normalizeRoutes above.
            try {
                if (match(p.split('?')[0], { decode: decodeURIComponent })(location.pathname))
                    return reason;
            }
            catch (_a) {
                continue;
            }
        }
        return clientUnavailableReason(matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config);
        // isMobile: desktopOnly pages switch when a tablet turns
    }, [context.unavailableRoutes, location.pathname, matchedRoute, isMobile]);
    // Anonymous = no real user session. Use the immutable isPublicSession flag set at context init,
    // not userKey/apiKey which mutate after setPublicCredentials fires for public dashboards.
    const isAnonymous = context.isPublicSession;
    const isAllowed = useMemo(() => {
        var _a;
        if (isAnonymous) {
            // Anonymous users can only access routes explicitly marked isPublic.
            // Dashboard routes use portal-level IsPublic — handled after portal loads.
            if (resolvedDashboardPath)
                return !dashboardDenied; // denied dashboards (incl. anonymous on non-public) are redirected to login by App
            return !!((_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.isPublic);
        }
        // Backend filters routes to only those the user can access before sending them.
        // Any route present in the config is already allowed for this user.
        return !!matchedRoute;
    }, [matchedRoute, isAnonymous, resolvedDashboardPath, dashboardDenied]);
    /*
     * Handle redirects. Checks config.redirectTo (set by the server) and
     * config.metadata.redirect.to (set when the nav link type is 'redirect').
     */
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        const redirectTo = (_b = (_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.redirectTo) !== null && _b !== void 0 ? _b : (_e = (_d = (_c = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _c === void 0 ? void 0 : _c.metadata) === null || _d === void 0 ? void 0 : _d.redirect) === null || _e === void 0 ? void 0 : _e.to;
        if (redirectTo && isAllowed)
            navigate(redirectTo, { replace: true });
    }, [matchedRoute, isAllowed, navigate]);
    // isLoading drives <BaseLayoutSkeleton /> in App — only signal it on the initial
    // dashboard load (no existing config). Sub-dashboard navigations should not
    // unmount the layout; DashboardView handles its own per-content loading.
    return { matchedRoute, isAllowed, isRestricted, isDashboard, isLoading: dashboardPending && !dashboardConfig, isAnonymous, unavailableReason };
}
